import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Tooltip } from "antd";
import useCmsMenus from "../cms/hooks/useCmsMenus";
import { useInboxBadges } from "../cms/hooks/useInboxBadges";
import {
  selectInboxUnreadCount, selectLeadInboxUnreadCount, selectTicketUnreadCount, selectSidebarCollapsed,
} from "../store/auth/authSlice";

/* ── Brand colors ──
   The accent follows the theme, which is shared with the intelligence
   dashboard. A CSS variable works in inline styles as well as in the
   stylesheet, so both paths stay on one value. */
const BRAND = "var(--em-accent)";



/* ── Label overrides (API name → display name) ── */
const LABEL_OVERRIDES = {
  "/cms/manageenquiry/done":              "Done",
  "/cms/manageenquiry/negative-enquiry":  "Negative",
};

/* ── Route overrides (API path → actual frontend path) ── */
const ROUTE_OVERRIDES = {
  "/cms/manageenquiry/done":              "/cms/manageenquiry/done",
  "/cms/manageenquiry/negative-enquiry":  "/cms/manageenquiry/negative-enquiry",
};

/* Resolve a sub-menu link to its (possibly overridden) frontend path */
const resolveLink = (link) => ROUTE_OVERRIDES[link] || link;

/**
 * A menu row that may point outside the CMS.
 *
 * The menu list is admin-authored, so a link can be a full URL to another tool
 * as easily as an internal route. Routed through <Link> a URL would be treated
 * as a path and land on "/cms/http://…", so an absolute one becomes a real
 * anchor and opens in its own tab — this app stays where it is.
 */
const isExternal = (link) => /^https?:\/\//i.test(String(link || ""));

const MenuLink = ({ to, children, ...rest }) =>
  isExternal(to)
    ? <a href={to} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>
    : <Link to={to} {...rest}>{children}</Link>;

/* ── Icon colors for specific sub-menu routes ── */
const SUB_ICON_COLORS = {
  "/cms/payment-list": "#6366f1",
  "/cms/tickets": "#6366f1",
  "/cms/manage-cms-menus": "#2563eb",
  "/cms/manage-roles": "#8b5cf6",
  "/cms/manage-permissions": "#8b5cf6",
  "/cms/manage-team-members": "#0891b2",
  "/cms/manage-services": "#0d9488",
  "/cms/lead-assigned-master": "#16a34a",
  "/cms/rate-card": "#5b48e8",
  "/cms/proposal-content": "#ea580c",
  "/cms/companyinformation": "#0284c7",
  "/cms/proposals": "#7c3aed",
  "/cms/proposals/create": "#7c3aed",
  "/cms/quotations": "#0d9488",
  "/cms/quotations/create": "#0d9488",
  "/leads/unassigned-leads": "#d97706",
  "/leads/assigned-leads": "#0891b2",
  "/cms/manage-customer/manage-order": "#6366f1",
  "/cms/manage-customer/active-free-trial": "#0891b2",
  "/cms/manage-customer/active-paid": "#16a34a",
  "/cms/manage-customer/pending-payment": "#f59e0b",
  "/cms/manage-customer/expiring-soon": "#ea580c",
  "/cms/manage-customer/paid-expired": "#dc2626",
  "/cms/manage-customer/free-trial-expired": "#9333ea",
  "/cms/manage-customer/subscription-inbox": "#2563eb",
  "/cms/inbox": "#2563eb",
  "/cms/manageenquiry/inbox": "#2563eb",
  "/cms/manageenquiry/future": "#f59e0b",
  "/cms/manageenquiry/done":     "#16a34a",
  "/cms/manageenquiry/negative-enquiry": "#dc2626",
  "/cms/manageenquiry/add-enquiry": "#16a34a",
  "/cms/templates": "#667eea",
  "/cms/templates/email/create": "#667eea",
  "/cms/templates/global": "#4338ca",
};

const normalizeIcon = (icon) => {
  if (!icon) return "";
  if (/^fa[brs]?\s/.test(icon)) return icon;
  return `fas ${icon}`;
};

const INBOX_ROUTE = "/cms/inbox";
const LEAD_INBOX_ROUTE = "/cms/manageenquiry/inbox";
const TICKETS_ROUTE = "/cms/tickets";

const LeftSideBar = () => {
  const { data: menus = [] } = useCmsMenus();
  const pathname = useLocation().pathname;
  const inboxUnreadCount = useSelector(selectInboxUnreadCount);
  const leadInboxUnreadCount = useSelector(selectLeadInboxUnreadCount);
  const ticketUnreadCount = useSelector(selectTicketUnreadCount);
  const isMini = useSelector(selectSidebarCollapsed);

  /* ── Global unread badges: seed on mount, SSE for live updates, focus to reconcile ── */
  useInboxBadges();

  const [openDropdowns, setOpenDropdowns] = useState({});
  const [activeParentMenu, setActiveParentMenu] = useState(null);
  const [isSubMenuSidebarOpen, setIsSubMenuSidebarOpen] = useState(false);
  const prevPathRef = useRef(pathname);

  /* ── STRICT exact match — fixes the "both highlighted" bug ── */
  const isSubActive = useCallback((link) => {
    if (!link) return false;
    // ONLY exact match — no startsWith, no includes
    return pathname === link || pathname === link + "/";
  }, [pathname]);

  const isMenuActive = useCallback((path) => {
    if (!path) return false;
    return pathname === path || pathname === path + "/" || pathname.startsWith(path + "/");
  }, [pathname]);

  /* ── Toggle single dropdown ── */
  const toggleDropdown = useCallback((menuId) => {
    setOpenDropdowns((prev) => (prev[menuId] ? {} : { [menuId]: true }));
  }, []);

  /* ── Auto-expand dropdown + sync secondary sidebar on route/menu change ── */
  useEffect(() => {
    if (menus.length === 0) return;

    const pathChanged = prevPathRef.current !== pathname;
    if (pathChanged) prevPathRef.current = pathname;

    const toOpen = {};
    let foundParent = null;

    menus.forEach((menu) => {
      if (menu.sub_menus?.length > 0) {
        const hasActive = menu.sub_menus.some((sm) => {
          const link = resolveLink(sm.sub_menu_link);
          return link && (pathname === link || pathname === link + "/");
        });
        if (hasActive) {
          toOpen[menu.id] = true;
          foundParent = menu;
        }
      }
    });

    // Auto-expand dropdown only when path actually changed
    if (pathChanged) setOpenDropdowns(toOpen);

    // In collapsed mode: always sync secondary sidebar (covers initial load + navigation)
    if (isMini) {
      if (foundParent) {
        setActiveParentMenu(foundParent);
        setIsSubMenuSidebarOpen(true);
      } else if (pathChanged) {
        // Only close on actual navigation to a non-submenu page
        setActiveParentMenu(null);
        setIsSubMenuSidebarOpen(false);
      }
    }
  }, [pathname, menus, isMini]);

  /* ── Add/remove body class so CSS can push main content ── */
  useEffect(() => {
    if (isSubMenuSidebarOpen && isMini) {
      document.body.classList.add("cms-submenu-sidebar-open");
    } else {
      document.body.classList.remove("cms-submenu-sidebar-open");
    }
    return () => {
      document.body.classList.remove("cms-submenu-sidebar-open");
    };
  }, [isSubMenuSidebarOpen, isMini]);

  /* ── Sync body class FROM Redux state (one-way: Redux → DOM) ── */
  useEffect(() => {
    if (isMini) {
      document.body.classList.add("vertical-collpsed");
    } else {
      document.body.classList.remove("vertical-collpsed");
      setActiveParentMenu(null);
      setIsSubMenuSidebarOpen(false);
    }
  }, [isMini]);

  /* ── Handle parent click in collapsed mode ── */
  const handleParentMenuClick = useCallback((menu) => {
    if (activeParentMenu?.id === menu.id && isSubMenuSidebarOpen) {
      setIsSubMenuSidebarOpen(false);
      setActiveParentMenu(null);
    } else {
      setActiveParentMenu(menu);
      setIsSubMenuSidebarOpen(true);
    }
  }, [activeParentMenu, isSubMenuSidebarOpen]);

  /* ════════════════════════════════════════════════════
     COLLAPSED MODE — Icon only
     ════════════════════════════════════════════════════ */
  const renderCollapsedMenu = () =>
    menus.map((menu) => {
      const hasSubMenus = menu.sub_menus?.length > 0;
      const menuActive = !hasSubMenus && isSubActive(menu.menu_link);
      const anySubActive = hasSubMenus &&
        menu.sub_menus.some((sm) => isSubActive(resolveLink(sm.sub_menu_link)));
      const isIconActive = menuActive || anySubActive || activeParentMenu?.id === menu.id;

      return (
        <li key={menu.id} className="cms-collapsed-item">
          <Tooltip title={menu.menu_name} placement="right">
            {hasSubMenus ? (
              <div
                className={`cms-collapsed-link${isIconActive ? " active" : ""}`}
                onClick={() => handleParentMenuClick(menu)}
              >
                <i className={menu.menu_icon} />
              </div>
            ) : (
              <Link
                to={menu.menu_link}
                className={`cms-collapsed-link${isIconActive ? " active" : ""}`}
                style={{ position: "relative" }}
                onClick={() => {
                  setActiveParentMenu(null);
                  setIsSubMenuSidebarOpen(false);
                }}
              >
                <i className={menu.menu_icon} />
                {menu.menu_link === INBOX_ROUTE && inboxUnreadCount > 0 && (
                  <span style={{
                    position: "absolute", top: 6, right: 8,
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#e74856", border: "1.5px solid #fff",
                  }} />
                )}
                {menu.menu_link === TICKETS_ROUTE && ticketUnreadCount > 0 && (
                  <span style={{
                    position: "absolute", top: 6, right: 8,
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#e74856", border: "1.5px solid #fff",
                  }} />
                )}
                {menu.menu_link === LEAD_INBOX_ROUTE && leadInboxUnreadCount > 0 && (
                  <span style={{
                    position: "absolute", top: 6, right: 8,
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#e74856", border: "1.5px solid #fff",
                  }} />
                )}
              </Link>
            )}
          </Tooltip>
        </li>
      );
    });

  /* ════════════════════════════════════════════════════
     SECONDARY SIDEBAR — Submenu panel (collapsed mode)
     ════════════════════════════════════════════════════ */
  const renderSecondarySidebar = () => {
    if (!activeParentMenu || !isSubMenuSidebarOpen || !isMini) return null;
    const subMenus = activeParentMenu.sub_menus || [];

    return (
      <div className="cms-secondary-sidebar">
        <div className="cms-secondary-header">
          <h6 className="cms-secondary-title">{activeParentMenu.menu_name}</h6>
        </div>
        <ul className="cms-secondary-menu">
          {subMenus.map((sub) => {
            const resolvedLink = resolveLink(sub.sub_menu_link);
            const sActive = isSubActive(resolvedLink);
            const iconColor = SUB_ICON_COLORS[resolvedLink];
            return (
              <li key={sub.id} className={`cms-secondary-item${sActive ? " active" : ""}`}>
                <MenuLink to={resolvedLink} className="cms-secondary-link">
                  <i
                    className={`cms-secondary-icon ${normalizeIcon(sub.sub_menu_icon) || "fas fa-angle-right"}`}
                    style={{ color: sActive ? BRAND : (iconColor || "#9ca3af") }}
                  />
                  <span className="cms-secondary-text">
                    {LABEL_OVERRIDES[sub.sub_menu_link] || sub.sub_menu_name}
                  </span>
                  {resolvedLink === INBOX_ROUTE && inboxUnreadCount > 0 && (
                    <span style={{
                      background: "#e74856", color: "#fff",
                      borderRadius: 10, padding: "1px 7px",
                      fontSize: 11, fontWeight: 700, marginLeft: 6, flexShrink: 0,
                    }}>{inboxUnreadCount}</span>
                  )}
                  {resolvedLink === LEAD_INBOX_ROUTE && leadInboxUnreadCount > 0 && (
                    <span style={{
                      background: "#e74856", color: "#fff",
                      borderRadius: 10, padding: "1px 7px",
                      fontSize: 11, fontWeight: 700, marginLeft: 6, flexShrink: 0,
                    }}>{leadInboxUnreadCount}</span>
                  )}
                  {resolvedLink === TICKETS_ROUTE && ticketUnreadCount > 0 && (
                    <span style={{
                      background: "#e74856", color: "#fff",
                      borderRadius: 10, padding: "1px 7px",
                      fontSize: 11, fontWeight: 700, marginLeft: 6, flexShrink: 0,
                    }}>{ticketUnreadCount}</span>
                  )}
                </MenuLink>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  /* ════════════════════════════════════════════════════
     EXPANDED MODE — Full sidebar
     ════════════════════════════════════════════════════ */
  const renderExpandedMenu = () =>
    menus.map((menu) => {
      const hasSubMenus = menu.sub_menus?.length > 0;
      const menuActive = !hasSubMenus && isSubActive(menu.menu_link);
      const anySubActive = hasSubMenus &&
        menu.sub_menus.some((sm) => isSubActive(resolveLink(sm.sub_menu_link)));
      const isOpen = !!openDropdowns[menu.id];

      if (!hasSubMenus) {
        const isInbox = menu.menu_link === INBOX_ROUTE;
        const isTickets = menu.menu_link === TICKETS_ROUTE;
        return (
          <li key={menu.id} className="cms-menu-item">
            <MenuLink to={menu.menu_link} className={`cms-menu-link${menuActive ? " active" : ""}`}>
              <i className={`cms-menu-icon ${menu.menu_icon}`} style={{ color: menuActive ? BRAND : "#6b7280" }} />
              <span className="cms-menu-text">{menu.menu_name}</span>
              {isInbox && inboxUnreadCount > 0 && (
                <span style={{
                  background: "#e74856", color: "#fff",
                  borderRadius: 10, padding: "1px 7px",
                  fontSize: 11, fontWeight: 700, marginLeft: 6, flexShrink: 0,
                }}>{inboxUnreadCount}</span>
              )}
              {isTickets && ticketUnreadCount > 0 && (
                <span style={{
                  background: "#e74856", color: "#fff",
                  borderRadius: 10, padding: "1px 7px",
                  fontSize: 11, fontWeight: 700, marginLeft: 6, flexShrink: 0,
                }}>{ticketUnreadCount}</span>
              )}
            </MenuLink>
          </li>
        );
      }

      return (
        <li key={menu.id} className="cms-menu-item">
          <div
            className={`cms-menu-link has-dropdown${anySubActive ? " active" : ""}`}
            onClick={() => toggleDropdown(menu.id)}
          >
            <i className={`cms-menu-icon ${menu.menu_icon}`} style={{ color: anySubActive ? BRAND : "#6b7280" }} />
            <span className="cms-menu-text">{menu.menu_name}</span>
            <i className={`fas fa-chevron-down cms-menu-arrow${isOpen ? " rotated" : ""}`} />
          </div>
          <ul className={`cms-submenu-list${isOpen ? " open" : ""}`}>
            {menu.sub_menus.map((sub) => {
              const resolvedLink = resolveLink(sub.sub_menu_link);
              const sActive = isSubActive(resolvedLink);
              const iconColor = SUB_ICON_COLORS[resolvedLink];
              return (
                <li key={sub.id} className="cms-submenu-item">
                  <MenuLink to={resolvedLink} className={`cms-submenu-link${sActive ? " active" : ""}`}>
                    <i
                      className={`cms-submenu-icon ${normalizeIcon(sub.sub_menu_icon) || "fas fa-angle-right"}`}
                      style={{ color: sActive ? BRAND : (iconColor || "#9ca3af") }}
                    />
                    <span>{LABEL_OVERRIDES[sub.sub_menu_link] || sub.sub_menu_name}</span>
                    {resolvedLink === INBOX_ROUTE && inboxUnreadCount > 0 && (
                      <span style={{
                        background: "#e74856", color: "#fff",
                        borderRadius: 10, padding: "1px 7px",
                        fontSize: 11, fontWeight: 700, marginLeft: 6, flexShrink: 0,
                      }}>{inboxUnreadCount}</span>
                    )}
                    {resolvedLink === LEAD_INBOX_ROUTE && leadInboxUnreadCount > 0 && (
                      <span style={{
                        background: "#e74856", color: "#fff",
                        borderRadius: 10, padding: "1px 7px",
                        fontSize: 11, fontWeight: 700, marginLeft: 6, flexShrink: 0,
                      }}>{leadInboxUnreadCount}</span>
                    )}
                    {resolvedLink === TICKETS_ROUTE && ticketUnreadCount > 0 && (
                      <span style={{
                        background: "#e74856", color: "#fff",
                        borderRadius: 10, padding: "1px 7px",
                        fontSize: 11, fontWeight: 700, marginLeft: 6, flexShrink: 0,
                      }}>{ticketUnreadCount}</span>
                    )}
                  </MenuLink>
                </li>
              );
            })}
          </ul>
        </li>
      );
    });

  return (
    <>
      <div
        className={`vertical-menu${isMini ? " cms-mini-mode" : ""}`}
        style={{
          background: "#ffffff",
          overflowX: "hidden",
        }}
      >
        <div data-simplebar className="h-100">
          {/* ── Menu list ── */}
          <ul className="cms-sidebar-menu">
            {isMini ? renderCollapsedMenu() : renderExpandedMenu()}
          </ul>
        </div>
      </div>

      {renderSecondarySidebar()}
    </>
  );
};

export default LeftSideBar;







//Working one
// import React, { useEffect, useState } from "react";
// import { Link, useLocation } from "react-router-dom";
// import useCmsMenus from "../cms/hooks/useCmsMenus";

// /* ── Icon colors for specific sub-menu routes (matches static sidebar) ── */
// const SUB_ICON_COLORS = {
//   "/cms/manage-cms-menus":               "#2563eb",
//   "/cms/manage-roles":                    "#8b5cf6",
//   "/cms/manage-permissions":              "#8b5cf6",
//   "/cms/manage-team-members":             "#0891b2",
//   "/cms/manageenquiry/inbox":             "#2563eb",
//   "/cms/manageenquiry/future":            "#f59e0b",
//   "/cms/manageenquiry/done":              "#16a34a",
//   "/cms/manageenquiry/negative-enquiry":  "#dc2626",
//   "/cms/templates/email/create":          "#667eea",
// };

// /* Links that require exact-match for active highlighting */
// const EXACT_MATCH_LINKS = ["/cms/templates", "/cms/manageenquiry"];

// /* Normalize sub-menu icon class (add "fas" prefix when missing) */
// const normalizeIcon = (icon) => {
//   if (!icon) return "";
//   if (/^fa[brs]?\s/.test(icon)) return icon;
//   return `fas ${icon}`;
// };

// const LeftSideBar = () => {
//   const { data: menus = [] } = useCmsMenus();
//   const [openDropdowns, setOpenDropdowns] = useState({});
//   const pathname = useLocation().pathname;

//   const getActiveClass = (path) => {
//     if (!path) return "";
//     if (pathname.includes(path)) return "active";
//     return "";
//   };

//   /* Exact-match aware active class for sub-menu items */
//   const getSubMenuActiveClass = (link) => {
//     if (!link) return "";
//     if (EXACT_MATCH_LINKS.includes(link)) {
//       return pathname === link || pathname === link + "/" ? "active" : "";
//     }
//     return getActiveClass(link);
//   };

//   /* Toggle a dropdown — close all others, open/close this one */
//   const toggleDropdown = (menuId) => {
//     setOpenDropdowns((prev) => {
//       if (prev[menuId]) return {};           // already open → close all
//       return { [menuId]: true };             // close all, open this one
//     });
//   };

//   /* Auto-expand the correct dropdown when navigating to a route */
//   useEffect(() => {
//     if (menus.length === 0) return;
//     const toOpen = {};
//     menus.forEach((menu) => {
//       if (menu.sub_menus?.length > 0) {
//         const links = menu.sub_menus.map((sm) => sm.sub_menu_link).filter(Boolean);
//         if (links.some((link) => pathname.startsWith(link) || pathname === link)) {
//           toOpen[menu.id] = true;
//         }
//       }
//     });
//     if (Object.keys(toOpen).length > 0) {
//       setOpenDropdowns((prev) => ({ ...prev, ...toOpen }));
//     }
//   }, [pathname, menus]);

//   return (
//     <div className="vertical-menu">
//       <div data-simplebar className="h-100">
//         <div id="sidebar-menu">
//           <ul className="metismenu list-unstyled" id="side-menu">
//             {menus.map((menu) => {
//               const isOpen = !!openDropdowns[menu.id];
//               const hasSubMenus = menu.sub_menus?.length > 0;

//               /* Plain link (no sub-menus) */
//               if (!hasSubMenus) {
//                 return (
//                   <li key={menu.id} className={getActiveClass(menu.menu_link)}>
//                     <Link
//                       to={menu.menu_link}
//                       className="waves-effect d-flex align-items-center py-2 px-3"
//                     >
//                       <i className={`${menu.menu_icon} me-3`}></i>
//                       <span>{menu.menu_name}</span>
//                     </Link>
//                   </li>
//                 );
//               }

//               /* Dropdown menu */
//               return (
//                 <li key={menu.id} className="nav-item">
//                   <a
//                     href={`#menu${menu.id}Collapse`}
//                     className="nav-link d-flex align-items-center justify-content-between py-2 px-3 waves-effect"
//                     data-bs-toggle="collapse"
//                     aria-expanded={isOpen}
//                     onClick={(e) => { e.preventDefault(); toggleDropdown(menu.id); }}
//                     style={{ cursor: "pointer" }}
//                   >
//                     <div className="d-flex align-items-center">
//                       <i className={`${menu.menu_icon} me-3`}></i>
//                       <span>{menu.menu_name}</span>
//                     </div>
//                     <i className={`fas fa-chevron-${isOpen ? "up" : "down"} ms-2`}></i>
//                   </a>
//                   <div className={`collapse ${isOpen ? "show" : ""}`}>
//                     <ul className="list-unstyled ps-4 mb-0">
//                       {menu.sub_menus.map((sub) => {
//                         const iconColor = SUB_ICON_COLORS[sub.sub_menu_link];
//                         const activeClass = getSubMenuActiveClass(sub.sub_menu_link);
//                         return (
//                           <li key={sub.id} className={`nav-item ${activeClass}`}>
//                             <Link
//                               to={sub.sub_menu_link}
//                               className={`nav-link d-flex align-items-center py-2 px-3 ${activeClass}`}
//                             >
//                               <i
//                                 className={`${normalizeIcon(sub.sub_menu_icon) || "fas fa-angle-right"} me-3${!iconColor ? " text-muted" : ""}`}
//                                 style={iconColor ? { color: iconColor } : {}}
//                               ></i>
//                               <span>{sub.sub_menu_name}</span>
//                             </Link>
//                           </li>
//                         );
//                       })}
//                     </ul>
//                   </div>
//                 </li>
//               );
//             })}
//           </ul>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default LeftSideBar;


